import { createSlice } from '@reduxjs/toolkit';
import { getGlobalExchanges, updateGlobalExchange } from './GlobalExchanges.thunk';
import { exchangeRowId } from '../../../utils/exchangeIdentity';

const initialState = {
  exchanges: [],
  status: 'idle',
  statusUpdate: 'idle',
  error: null,
  page: 1,
  pageSize: 5,
  totalRecords: 0,
};

const getActive = (exchangeStatus) => {
  return exchangeStatus === 'E';
};

export const globalExchangesSlice = createSlice({
  name: 'globalExchanges',
  initialState,
  reducers: {
    setGlobalExchanges(state, action) {
      state.exchanges = action.payload;
      state.status = 'succeeded';
    },
    setGlobalExchangesLoading(state) {
      state.status = 'loading';
    },
    setGlobalExchangesError(state, action) {
      state.status = 'failed';
      state.error = action.payload;
    },
    modifyGlobalExchange(state, action) {
      const updated = action.payload;
      state.exchanges = state.exchanges.map(e =>
        e.id === updated.id ? { ...e, ...updated } : e
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGlobalExchanges.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getGlobalExchanges.fulfilled, (state, action) => {
        const { exchanges, page, pageSize, totalRecords } = action.payload;
        state.exchanges = (exchanges || []).map((e) => ({
          catalogId: e.id ?? null,
          id: exchangeRowId({
            catalogId: e.id,
            exchangeID: e.exchangeID,
            category: e.category,
            exchangeName: e.exchangeName,
          }),
          icon: e.exchangeLogoURL || null,
          code: e.exchangeID || '',
          name: e.exchangeName || "-",
          category: e.category || 'exchanges',
          position: e.position || '',
          currencies: e.currencies || [],
          status: e.status,
          active: !!getActive(e.status),
          kyc_identity_enabled: !!e.kyc_identity_enabled,
          kyc_provider_code: e.kyc_provider_code || "",
          kyc_provider_account_key: e.kyc_provider_account_key || "",
          kyc_identity_threshold_usd:
            e.kyc_identity_threshold_usd != null ? e.kyc_identity_threshold_usd : "",
          aml: !!e.aml,
        }));
        state.page = page || 1;
        state.pageSize = pageSize || 5;
        state.totalRecords = totalRecords || state.exchanges.length;
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(getGlobalExchanges.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Error getting exchanges";
        state.exchanges = [];
      });

    builder
      .addCase(updateGlobalExchange.pending, (state) => {
        state.statusUpdate = "loading";
        state.error = null;
      })
      .addCase(updateGlobalExchange.fulfilled, (state, action) => {
        const { exchangeRowKey, exchangeStatus, position, currencies } = action.payload;
        state.exchanges = state.exchanges.map((e) =>
          e.id === exchangeRowKey ? {
            ...e,
            active: getActive(exchangeStatus),
            position: position || '',
            currencies: currencies || [],
            status: exchangeStatus
          } : e
        );
        state.statusUpdate = "succeeded";
        state.error = null;
      })
      .addCase(updateGlobalExchange.rejected, (state, action) => {
        state.statusUpdate = "failed";
        state.error = action.payload?.message || "Error modifying exchange";
      });
  },
});

export const {
  setGlobalExchanges,
  setGlobalExchangesLoading,
  setGlobalExchangesError,
  modifyGlobalExchange,
} = globalExchangesSlice.actions;

export default globalExchangesSlice.reducer;
